import * as _ from 'lodash';
import { RemoteControlCloseTypes } from '../common/constants/remote-control';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { SalesforceSaveObject } from 'shared/components/salesforce-lane/salesforce-form/model/salesforce-form.model';
import { GetSalesforceReferenceItemsResult } from './models/salesforce.models';
import { PinType } from '../workitems/constants/pin-types';
import { NanorepAccountInfoModel } from 'common/api/models/nanorep-account-info.model';
import { OperatorNamesList } from './models/operator-names-list';
import { Settings } from '../settings/settings.interface';
import { WrapUp } from './models/wrap-up.model';
import { AccountVersionInfoModel } from './models/account-version-info.model';
import { CurrentUserModel } from './models/current-user.model';
import { ServerEndpointsModel } from './models/server-endpoints.model';
import { AssigneeTypes } from '../common/constants/boldchat-enums';
import { InitDiscussionResponseModel } from './models/init-discussion-response.model';
import { ActiveWorkItemCountModel } from './models/active-work-item-count.model';
import { OperatorInitialGreeting } from './models/operator-initial-greeting';
import { OperatorInitialGreetingSettings } from './models/operator-initial-greeting-settings';
import { ChatWindowSettings } from './models/chat-window-settings.model';
import { CsrfTokenModel } from './models/csrf-token.model';
import { VisitorData } from 'shared/models/visitor-info/visitor-data.interface';
import { IdentityLoginConfigurationModel } from 'common/api/models/identity-login-configuration.model';
import { AgentStat, AgentStatIndicator } from '../../components/accordion/agent-stats/models/agent-stat.model';
import { Injectable } from '@angular/core';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcConfigService } from 'common/config/provider';
import { TextChatMessage } from 'common/xislivechat/models/text-chat-message';
import { BcApiService, HttpRequestConfig } from 'common/api/api.service';
import { AssignmentInfo } from 'components/work-item-lane-list/work-item-lane/work-item-widget/chat-widget/chat-box/assignment-info.model';
import { AgentLoads } from 'common/api/models/agent-loads.model';
import { AgentCapacities } from 'common/api/models/agent-capacities.model';
import { RawUserPermissionModel } from 'shared/services/user-permissions/user-permission.model';
import { DepartmentLoadsModel } from './models/department-loads.model';
import { ServiceTypes } from 'common/common/constants/service-types';
import { CriterionGroup } from 'shared/models/visitor-info/criterion-group';
import { ExperimentData } from 'shared/components/detailed-data/visit.interface';
import { AssignmentHistoryModel } from 'shared/components/assignment-history/models/assignment-history.model';
import { LogModel } from 'common/logger/model/log.model';
import { AccountCountModel } from 'common/api/models/account-count.model';
import { UrlModel } from 'common/api/models/url.model';
import { VendorVideoSessionResponse } from 'common/api/models/vendor-video-session-response.model';
import { VendorRemoteControlSessionResponse } from 'common/api/models/vendor-remote-control-session-response.model';
import { InnerHtmlPipe } from 'shared/components/canned-message-panel/filters/inner-html.pipe';
import { CsrfTokenService } from './csrf-token/csrf-token.service';

// The version of the API to call as it appears in the URLs.
const API_VERSION = 'v1';

interface ApiConfig extends HttpRequestConfig {
  accountAware?: boolean;
}

interface ApiParameters {
  body?: any;
  queryString?: any;
}

/**
 * Provides high-level functions to call endpoints on the REST API.
 */
@Injectable()
export class BcRestApi {
  private static readonly InnerHtmlPipe = new InnerHtmlPipe();
  private accountId: string;

  constructor(
    private _bcConfig: BcConfigService,
    private _bcApi: BcApiService,
    private _csrfTokenService: CsrfTokenService
  ) {
    this.resetAccountId();
  }

  async initApiForUser(params): Promise<ServerEndpointsModel> {
    if (this._csrfTokenService.needsCsrfTokenRequest) {
      const csrfTokenResponse = await this.getCsrfToken();
      this._csrfTokenService.set(csrfTokenResponse?.csrfToken);
    }
    return this.setServerEndpoints(params);
  }

  setServerEndpoints(params): Promise<ServerEndpointsModel> {
    return this.getServerEndpoints(params).then((serverEndpoints: ServerEndpointsModel) => {
      if (serverEndpoints.restApiEndpoint) {
        this._bcConfig.setRestApiUrl(serverEndpoints.restApiEndpoint.url);
      }
      if (serverEndpoints.uploadApiEndpoint) {
        this._bcConfig.setFileUploadHost(serverEndpoints.uploadApiEndpoint.url);
      }
      if (serverEndpoints.serverSetDomain) {
        this._bcConfig.setServerSetDomain(serverEndpoints.serverSetDomain);
      }
      if (serverEndpoints.accountId) {
        this.setAccountId(serverEndpoints.accountId);
      }
      return serverEndpoints;
    });
  }

  getAccountId(): string {
    return this.accountId;
  }

  setAccountId(accountId: string): void {
    this.accountId = accountId;
  }

  resetAccountId(): void {
    this.accountId = null;
  }

  isInitialized(): boolean {
    return !!this.accountId;
  }

  /**
   * Checks whether the server is available.
   * @returns {Promise<object>} The promise of the raw data returned by the server.
   */
  checkHealth() {
    return this._sendGet('health/check', undefined, { accountAware: false });
  }

  /**
   * Returns the folders of the specified folder type.
   * @param {number} folderType The unique identifier of the folder type. See the 'xlcModelsFolder' enum.
   * @returns {Promise.<Object>} The promise of the raw data returned by the server.
   * @throws {Error} If folderType is not specified.
   */
  getFolders(folderType) {
    if (!folderType) {
      throw new Error('Please specify folderType!');
    }

    let folderTypeParams = { type: folderType };

    return this._sendGet('folders', { queryString: folderTypeParams });
  }

  /**
   * Returns the folder's id, name and parentId of the specified folder.
   * @param {number} folderId The unique identifier of the folder.
   * @returns {Promise.<Object>} The promise of the raw data returned by the server.
   * @throws {Error} If folderId is not specified.
   */
  getFolder(folderId) {
    if (!folderId) {
      throw new Error('Please specify folderId!');
    }

    const url = `folders/${folderId}`;
    return this._sendGet(url);
  }

  getSmartResponses(record) {
    let url;

    if (typeof record.ChatID !== 'undefined') {
      url = `chats/${record.ChatID}/smart-responses`;
    } else if (typeof record.TextChatID !== 'undefined') {
      url = `text-chats/${record.TextChatID}/smart-responses`;
    }

    return this._sendGet(url);
  }

  getChatWindowSettings(chatWindowDefId): Promise<ChatWindowSettings> {
    if (!chatWindowDefId) {
      chatWindowDefId = 'default';
    }

    const url = `chat-window/${chatWindowDefId}/settings`;
    return this._sendGet(url, undefined, { skipHttpInterceptor: true });
  }

  getChatCannedMessages(chatWindowDefId) {
    if (!chatWindowDefId) {
      chatWindowDefId = 'default';
    }

    const url = `chat-window/${chatWindowDefId}/canned-messages`;
    return this._sendGet(url);
  }

  getChatCannedMessage(chatId, cannedMessageId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    if (!cannedMessageId) {
      throw new Error('Please specify cannedMessageId!');
    }

    const url = `chats/${chatId}/canned-messages/${cannedMessageId}`;
    return this._sendGet(url);
  }

  getChatCannedMessageVariables(chatId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/canned-message-variables`;
    return this._sendGet(url);
  }

  getTextChatCannedMsgByTextChatId(textChatId) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/canned-messages`;
    return this._sendGet(url);
  }

  getTextChatCannedMsgVariables(textChatId) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/canned-message-variables`;
    return this._sendGet(url);
  }

  getTextChatCannedMsg(textChatId, cannedMessageId) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    if (!cannedMessageId) {
      throw new Error('Please specify cannedMessageId!');
    }

    const url = `text-chats/${textChatId}/canned-messages/${cannedMessageId}`;
    return this._sendGet(url);
  }

  getBuiltInWrapUps(type, workItemId): Promise<WrapUp[]> {
    if (!workItemId) {
      throw new Error('Please specify workItemId!');
    }
    const url = this.buildBuiltInWrapUpUrl(type, workItemId);
    return this._sendGet(url);
  }

  buildBuiltInWrapUpUrl(type, workItemId) {
    switch (type) {
      case WorkItemTypes.CHAT:
        return `chats/${workItemId}/built-in-wrap-ups`;
      case WorkItemTypes.EMAIL:
        return `email-threads/${workItemId}/built-in-wrap-ups`;
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.SMS:
      case WorkItemTypes.ASYNC_MESSAGE:
        return `text-chats/${workItemId}/built-in-wrap-ups`;
      default:
        throw new Error('Unknown type.');
    }
  }

  getCustomWrapUps(chatId): Promise<WrapUp[]> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/custom-wrap-ups`;
    return this._sendGet(url, undefined, { skipHttpInterceptor: true });
  }

  saveBuiltInWrapUps(type, workItemId, wrapUps) {
    this.checkSaveWrapUpParams(workItemId, wrapUps);

    const url = this.buildBuiltInWrapUpUrl(type, workItemId);
    return this._sendPost(url, { body: wrapUps });
  }

  saveCustomWrapUps(chatId, wrapUps) {
    this.checkSaveWrapUpParams(chatId, wrapUps);

    const url = `chats/${chatId}/custom-wrap-ups`;
    return this._sendPost(url, { body: wrapUps });
  }

  checkSaveWrapUpParams(chatId, wrapUps) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    if (!wrapUps || !_.isArray(wrapUps) || wrapUps.length === 0) {
      throw new Error('Please specify list of wrapup fields to save!');
    }
  }

  /**
   * Answers a given chat, assigning it to the currently logged in operator.
   * @param {string} chatId - The unique identifier of the chat to assign.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatId is not specified.
   */
  answerNextChat(chatId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/answer-next`;
    return this._sendPost(url);
  }

  /**
   * Answers a given chat, assigning it to the currently logged in operator.
   * @param {string} chatId - The unique identifier of the chat to assign.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatId is not specified.
   */
  answerChat(chatId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/answer`;
    return this._sendPost(url);
  }

  /** Ends the chat with the specified chatId.
   * @param {string} chatId - The unique identifier of the chat to end.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatId is not specified.
   */
  endChat(chatId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/end`;
    return this._sendPost(url);
  }

  /** Ends multiple chats with the specified chatIds.
   * @param {string} chatIds - The unique identifier of all the chats to end.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatIds is not specified.
   */
  endChats(chatIds: string[]): Promise<void> {
    if (!chatIds || !chatIds.length) {
      throw new Error('Please specify chatIds!');
    }

    const url = 'chats/bulk-end';
    return this._sendPost(url, { body: chatIds });
  }

  /** Ends the text chat with the specified textChatId.
   * @param {string} textChatId - The unique identifier of the chat to end.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If textChatId is not specified.
   */
  endTextChat(textChatId) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/end`;
    return this._sendPost(url);
  }

  /** Ends multiple textChats with the specified textChatIds.
   * @param {string} textChatIds - The unique identifier of all the textChats to end.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatIds is not specified.
   */
  endTextChats(textChatIds: string[]): Promise<void> {
    if (!textChatIds || !textChatIds.length) {
      throw new Error('Please specify textChatIds!');
    }

    const url = 'text-chats/bulk-end';
    return this._sendPost(url, { body: textChatIds });
  }

  /** Log out multiple clients with the specified clientIds.
   * @param {string} clientIds - The unique identifier of all the clients to log out.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If clientIds is not specified.
   */
  logOutClients(clientIds: string[]): Promise<void> {
    if (!clientIds || !clientIds.length) {
      throw new Error('Please specify clientIds!');
    }

    const url = 'operators/bulk-logout';
    return this._sendPost(url, { body: clientIds });
  }

  /**
   * Set chat typing status
   * @param {string} chatId - The unique identifier of the chat to assign.
   * @param {boolean} isTyping - Is operator typing now or not.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatId is not specified or isTyping is undefined.
   */
  setTypingChat(chatId, isTyping) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    if (typeof isTyping === 'undefined') {
      throw new Error('Please specify isTyping!');
    }

    const typingModel = { isTyping };
    const url = `chats/${chatId}/typing`;
    return this._sendPost(url, { body: typingModel });
  }

  /**
   * Answers a given text chat, assigning it to the currently logged in operator.
   * @param {string} textChatId - The unique identifier of the text chat to assign.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If textChatId is not specified.
   */
  answerTextChat(textChatId) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/answer`;
    return this._sendPost(url);
  }

  /**
   * Set text chat typing status
   * @param {string} textChatId - The unique identifier of the text chat to set.
   * @param {boolean} isTyping - Is operator typing now or not.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If textChatId is not specified or isTyping is undefined.
   */
  setTypingTextChat(textChatId, isTyping) {
    if (!textChatId) {
      throw new Error('Please specify chatId!');
    }

    if (typeof isTyping === 'undefined') {
      throw new Error('Please specify isTyping!');
    }

    const typingModel = { isTyping };
    const url = `text-chats/${textChatId}/typing`;
    return this._sendPost(url, { body: typingModel });
  }

  /**
   * Set text chat user's email
   * @param {string} textChatId - The unique identifier of the text chat to set.
   * @param {boolean} email - New email for the text chat
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If textChatId is not specified or isTyping is undefined.
   */
  setTextChatEmail(textChatId, email) {
    if (!textChatId) {
      throw new Error('Please specify chatId!');
    }

    if (typeof email === 'undefined') {
      throw new Error('Please specify email!');
    }

    const emailModel = { email };
    const url = `text-chats/${textChatId}/change-email`;
    return this._sendPost(url, { body: emailModel });
  }

  /**
   * Set text chat user's phone
   * @param {string} textChatId - The unique identifier of the text chat to set.
   * @param {boolean} phone - New phone for the text chat
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If textChatId is not specified or isTyping is undefined.
   */
  setTextChatPhone(textChatId, phone) {
    if (!textChatId) {
      throw new Error('Please specify chatId!');
    }

    if (typeof phone === 'undefined') {
      throw new Error('Please specify phone!');
    }

    const phoneModel = { phone };
    const url = `text-chats/${textChatId}/change-phone`;
    return this._sendPost(url, { body: phoneModel });
  }

  /**
   * Set the availability of the currently logged in operator in the specified service.
   * @param {Number} serviceType - The type of the service. See more: bcEnums
   * @param {Boolean} isAvailable - Boolean value of the availability.
   * @returns {Promise} The promise for the result of the HTTP call.
   */
  setAvailability(serviceType, isAvailable) {
    const apiMethod = 'users/me/availability';
    const bodyParams = {};
    const serviceName = this._getServiceNameForAvailabilityEndpoint(serviceType);
    bodyParams[serviceName] = !!isAvailable;
    return this._sendPost(apiMethod, { body: bodyParams });
  }

  _getServiceNameForAvailabilityEndpoint(serviceType) {
    if (serviceType === bcEnums.services.CHATS) {
      return 'chat';
    } else if (serviceType === bcEnums.services.EMAILS) {
      return 'email';
    } else {
      return bcEnums.serviceNames[serviceType];
    }
  }

  setCustomStatus(customStatusId, serviceType) {
    const apiMethod = 'users/me/availability/custom';
    const bodyParams = {
      customStatusId,
      serviceType
    };
    return this._sendPost(apiMethod, { body: bodyParams });
  }

  /**
   * Set the availability of the currently logged in operator in the specified service.
   * @param {Number} serviceType - The type of the service. See more: bcEnums
   * @param {Boolean} isAvailable - Boolean value of the availability.
   * @returns {Promise} The promise for the result of the HTTP call.
   */
  getAllAvailabilities() {
    const apiMethod = 'users/me/custom-statuses';
    return this._sendGet(apiMethod);
  }

  getSetupToken() {
    const apiMethod = 'users/me/setup-token';
    return this._sendGet(apiMethod);
  }

  /**
   * Sends the specified chat message.
   * @param {Object} chatMessage The chat message to send.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatMessage is not specified.
   */
  sendMessage(chatMessage) {
    if (!chatMessage) {
      throw new Error('Please specify a chatMessage!');
    }

    const chatId = chatMessage.ChatID;
    const apiMethod = `chats/${chatId}/messages`;
    const bodyParams = {
      name: chatMessage.Name,
      text: chatMessage.Text,
      cannedMessageId: chatMessage.CannedMessageID
    };
    return this._sendPost(apiMethod, { body: bodyParams });
  }

  getChat(chatId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}`;
    return this._sendGet(url);
  }

  /**
   * Returns the messages that belong to the specified chat.
   * @param {string} chatId The unique identifier of the chat for which the messages should be returned.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If chatId is not specified.
   */
  getChatMessages(chatId) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/messages`;
    return this._sendGet(url);
  }

  /**
   * Sends the specified text chat message.
   * @param {Object} textChatMessage The text chat message to send.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If textChatMessage is not specified.
   */
  sendTextChatMessage(textChatMessage) {
    if (!textChatMessage) {
      throw new Error('Please specify a textChatMessage!');
    }

    const textChatId = textChatMessage.TextChatID;
    const apiMethod = `text-chats/${textChatId}/messages`;
    const bodyParams = {
      name: textChatMessage.Name,
      text: textChatMessage.Text,
      cannedMessageId: textChatMessage.CannedMessageID,
      jsonData: textChatMessage.JsonData
    };
    return this._sendPost(apiMethod, { body: bodyParams });
  }

  /**
   * Returns the messages that belong to the specified text chat.
   * @param {string} textChatId The unique identifier of the text chat for which the messages should be returned.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If textChatId is not specified.
   */
  getTextChatMessages(textChatId: string): Promise<TextChatMessage[]> {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/messages`;
    return this._sendGet(url);
  }

  getEmailThreadHistory(emailThreadId: string) {
    if (!emailThreadId) {
      throw new Error('Please specify emailThreadId!');
    }

    const url = `email-threads/${emailThreadId}/history`;
    return this._sendGet(url);
  }

  getEmailAccountSignature(emailAccountId: string) {
    if (!emailAccountId) {
      throw new Error('Please specify emailAccountId!');
    }

    const url = `users/me/email-accounts/${emailAccountId}/signature`;
    return this._sendGet(url);
  }

  /**
   * Executes a search over the provided services for simple matches to the provided search query.
   */
  search(bodyParams) {
    if (!bodyParams) {
      throw new Error('Please specify search parameters!');
    }

    const apiMethod = 'search';
    return this._sendPost(apiMethod, { body: bodyParams });
  }

  /**
   * Executes a fetch for the user/account settings.
   */
  getSettings(): Promise<Settings> {
    const url = 'users/me/settings';
    return this._sendGet(url);
  }

  /**
   * Returns the visitor by visitor id.
   * @param {string} visitorId The unique identifier of the visitor.
   * @returns {Promise.<VisitorData>} The promise of the visitor data returned by the server.
   * @throws {Error} If visitorId is not specified.
   */
  getVisitorByVisitorId(visitorId: string): Promise<VisitorData> {
    if (!visitorId) {
      throw new Error('Please specify visitorId!');
    }

    const url = `visitors/${visitorId}`;
    return this._sendGet(url);
  }

  getCampaignRules(experimentData: ExperimentData): Promise<CriterionGroup> {
    if (!experimentData.data) {
      return Promise.resolve(null);
    }
    const url = `experiments/${experimentData.data.experimentId}/criteria`;
    return this._sendGet(url);
  }

  /**
   * Returns the previous chat visits and emails of the specified visitor.
   * @param {string} chatId The unique identifier of the chat.
   * @returns {Promise.<Object>} The promise of the raw data returned by the server.
   * @throws {Error} If chatId is not specified.
   */
  getVisitorHistoryByChatId(chatId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/history`;
    return this._sendGet(url);
  }

  /**
   * Returns the previous chat visits and emails of the specified visitor.
   * @param {string} textChatId The unique identifier of the text chat.
   * @returns {Promise.<Object>} The promise of the raw data returned by the server.
   * @throws {Error} If textChatId is not specified.
   */
  getVisitorHistoryByTextChatId(textChatId: string) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/history`;
    return this._sendGet(url);
  }

  getCustomPreChatFields(chatId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/custom-pre-chat-fields`;
    return this._sendGet(url);
  }

  assignChatFromDiscussion(
    chatId: string,
    assigneeId: string,
    assigneeType: AssigneeTypes,
    assigneeDepartmentId: string
  ) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = `chats/${chatId}/assign-chat-from-discussion`;
    const type = assigneeType;
    const bodyParams = { type, id: undefined, assigneeDepartmentId };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  assignChat(chatId, assigneeId, assigneeType) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = `chats/${chatId}/assign`;
    const type = assigneeType;
    const bodyParams = { type, id: undefined };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  assignChats(chatIds: string[], assigneeId: string, assigneeType: string): Promise<void> {
    if (!chatIds || !chatIds.length) {
      throw new Error('Please specify chatIds!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = 'chats/bulk-assign';
    const type = assigneeType;

    const bodyParams = {
      chatIds,
      assignModel: {
        type,
        id: undefined
      }
    };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.assignModel.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  getChatAssignmentInfo(chatId: string): Promise<AssignmentInfo> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/assignment-info`;
    return this._sendGet(url);
  }

  assignTextChat(textChatId, assigneeId, assigneeType) {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = `text-chats/${textChatId}/assign`;
    const type = assigneeType;
    const bodyParams = { type, id: undefined };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  assignTextChats(textChatIds: string[], assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    if (!textChatIds || !textChatIds.length) {
      throw new Error('Please specify textChatIds!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = 'text-chats/bulk-assign';
    const type = assigneeType;

    const bodyParams = {
      textChatIds,
      assignModel: {
        type,
        id: undefined
      }
    };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.assignModel.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  assignEmail(emailId, assigneeId, assigneeType) {
    if (!emailId) {
      throw new Error('Please specify emailId!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = `email-threads/${emailId}/assign`;
    const type = assigneeType;
    const bodyParams = { type, id: undefined };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  assignEmails(emailThreadIds: string[], assigneeId: string, assigneeType: AssigneeTypes): Promise<void> {
    if (!emailThreadIds || !emailThreadIds.length) {
      throw new Error('Please specify emailThreadIds!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const apiMethod = 'email-threads/bulk-assign';
    const type = assigneeType;

    const bodyParams = {
      emailThreadIds,
      assignRequestModel: {
        type,
        id: undefined
      }
    };

    if (type !== AssigneeTypes.ACD) {
      bodyParams.assignRequestModel.id = assigneeId;
    }

    return this._sendPost(apiMethod, { body: bodyParams });
  }

  escalateChats(chatIds: string[], assigneeId: string, assigneeType: AssigneeTypes) {
    if (!chatIds) {
      throw new Error('Please specify chatIds!');
    }

    if (!assigneeId) {
      throw new Error('Please specify assigneeId!');
    }

    if (!assigneeType) {
      throw new Error('Please specify assigneeType!');
    }

    const url = 'chats/escalate';

    const bodyParams = {
      chatIds,
      assignModel: {
        id: assigneeId,
        type: assigneeType
      }
    };

    if (assigneeType === AssigneeTypes.ACD) {
      delete bodyParams.assignModel.id;
    }

    return this._sendPost(url, { body: bodyParams });
  }

  joinChat(chatId: string): Promise<void> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/join`;

    return this._sendPost(url);
  }

  leaveChat(chatId: string): Promise<void> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/leave`;

    return this._sendPost(url);
  }

  getEmailAccounts() {
    const url = 'users/me/email-accounts';
    return this._sendGet(url);
  }

  getCsrfToken(): Promise<CsrfTokenModel> {
    const url = 'authentication/csrfToken';
    return this._sendGet(url, undefined, { accountAware: false });
  }

  isAuthenticated() {
    const url = 'authentication/is-authenticated';
    return this._sendGet(url);
  }

  /**
   * Login with credentials (username, password).
   * @param {object} Contains `username` The unique identifier of the operator & `password` the operator password.
   * @param {boolean} Remember me token requested.
   * @returns {Empty} Returns nothing.
   * @throws {Error} If credentials is not specified.
   */
  login(credentials, rememberMe: boolean) {
    if (!credentials) {
      throw new Error('Please specify credentials!');
    }

    const url = 'authentication/login?remember-me=' + rememberMe;
    return this._sendPost(url, { body: credentials });
  }

  /**
   * Logout the current operator.
   * @returns {Empty} Returns nothing.
   */
  logout() {
    const url = 'authentication/logout';
    return this._sendPost(url);
  }

  getIdentityLoginConfiguration(
    redirectUrl: string,
    loginPath: string,
    logoutPath: string
  ): Promise<IdentityLoginConfigurationModel> {
    const url = 'authentication/identity-login-configuration';
    return this._sendPost(
      url,
      { body: { redirectUrl: redirectUrl, loginPath: loginPath, logoutPath: logoutPath } },
      { accountAware: false, skipHttpInterceptor: true }
    );
  }

  validateIdentityTicket(identityTicket: string): Promise<CsrfTokenModel> {
    const url = 'authentication/identity-login-validate';
    return this._sendPost(url, { body: { accountId: this.accountId, identityTicket: identityTicket } });
  }

  public getUserAccountCount(): Promise<AccountCountModel> {
    const url = 'authentication/account-count';
    return this._sendGet(url);
  }

  getCurrentUser(): Promise<CurrentUserModel> {
    const url = 'users/me';
    return this._sendGet(url);
  }

  getAccountVersion(): Promise<AccountVersionInfoModel> {
    const url = 'account/version';
    return this._sendGet(url);
  }

  createDynamicQuery(name: string, id: string, parameters: object): Promise<void> {
    const url = 'query';
    const params = { name, id, parameters };
    return this._sendPost(url, { body: params });
  }

  deleteDynamicQuery(queryId) {
    const url = `query/${queryId}`;
    return this._sendDelete(url);
  }

  /**
   * Returns details about the specified email thread.
   * @param {string} emailThreadId The unique identifier of the email thread for which the details should be returned.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If emailThreadId is not specified.
   */
  getEmailThread(emailThreadId) {
    if (!emailThreadId) {
      throw new Error('Please specify emailThreadId!');
    }

    const url = `email-threads/${emailThreadId}`;
    return this._sendGet(url);
  }

  reopenEmailThreads(emailThreadIds: string[]): Promise<void> {
    if (!emailThreadIds) {
      throw new Error('Please specify emailThreadIds!');
    }

    const url = 'email-threads/bulk-reopen';
    return this._sendPost(url, { body: emailThreadIds });
  }

  /**
   * Returns details about the specified email.
   * @param {string} emailId The unique identifier of the email for which the details should be returned.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If emailId is not specified.
   */
  getEmail(emailId) {
    if (!emailId) {
      throw new Error('Please specify emailId!');
    }

    const url = `emails/${emailId}`;
    return this._sendGet(url);
  }

  /**
   * Returns the last email from the specified email thread.
   * @param {string} emailThreadId The unique identifier of the email thread for which the details should be returned.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If emailThreadId is not specified.
   */
  getLastEmailFromThread(emailThreadId) {
    if (!emailThreadId) {
      throw new Error('Please specify emailThreadId!');
    }

    const url = `email-threads/${emailThreadId}/last-email`;
    return this._sendGet(url);
  }

  /**
   * Accepts an email thread.
   * @param {string} emailThreadId The unique identifier of the email thread.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If emailThreadId is not specified.
   */
  acceptEmailThread(emailThreadId) {
    if (!emailThreadId) {
      throw new Error('Please specify emailThreadId!');
    }

    const url = `email-threads/${emailThreadId}/accept`;
    return this._sendPost(url);
  }

  /**
   * Sends an e-mail into a given email thread.
   * Email address fields - from, to, cc, bcc - support both address@email.com and "John Doe" <address@email.com>
   * format.
   * @param {string} emailThreadId The unique identifier of the email thread.
   * @param {string} emailId The unique identifier of the original email.
   * @param {object} emailDetails The details of the email: from, to, cc, bcc, subject and htmlBody
   * @param {object} emailAttachments This contains an array of objects returned from sending attachments to the server
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If emailThreadId is not specified or messageModel is not specified.
   */
  sendMessageToEmailThread(emailThreadId, emailId, emailDetails, emailAttachments) {
    if (!emailThreadId) {
      throw new Error('Please specify emailThreadId!');
    }

    const bodyParams = this._generateCommonEmailBodyParams(emailDetails, emailAttachments);
    bodyParams.replyToEmailId = emailId;

    const url = `email-threads/${emailThreadId}/send`;
    return this._sendPost(url, { body: bodyParams });
  }

  sendNewEmailMessage(emailDetails, emailAttachments) {
    const bodyParams = this._generateCommonEmailBodyParams(emailDetails, emailAttachments);

    const url = 'email-threads';
    return this._sendPost(url, { body: bodyParams });
  }

  _generateCommonEmailBodyParams(emailDetails, emailAttachments) {
    const emailAccountId = emailDetails.senderEmailAccountId;
    const to = emailDetails.to;
    const cc = emailDetails.cc;
    const bcc = emailDetails.bcc;
    const subject = emailDetails.subject;
    const sanitizedEmailDetailsMessage = BcRestApi.InnerHtmlPipe.transform(emailDetails.message);
    const html =
      sanitizedEmailDetailsMessage && sanitizedEmailDetailsMessage.length > 0
        ? sanitizedEmailDetailsMessage
        : '<body></body>';

    // TODO: process messageModel properly
    return {
      emailAccountId,
      to,
      cc,
      bcc,
      subject,
      html,
      attachmentInfo: emailAttachments,
      replyToEmailId: undefined
    };
  }

  /**
   * Close an email thread.
   * @param {string} emailThreadId The unique identifier of the email thread.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If emailThreadId is not specified.
   */
  closeEmailThread(emailThreadId) {
    if (!emailThreadId) {
      throw new Error('Please specify emailThreadId!');
    }

    const url = `email-threads/${emailThreadId}/close`;
    return this._sendPost(url);
  }

  closeEmailThreads(emailThreadIds: string[]): Promise<void> {
    if (!emailThreadIds || !emailThreadIds.length) {
      throw new Error('Please specify emailThreadIds!');
    }

    const url = 'email-threads/bulk-close';
    return this._sendPost(url, { body: emailThreadIds });
  }

  getEmailCannedMsgByEmailId(emailId) {
    if (!emailId) {
      throw new Error('Please specify emailId!');
    }

    const url = `emails/${emailId}/canned-messages`;
    return this._sendGet(url);
  }

  getEmailCannedMsgByEmailAccountId(emailAccountId) {
    if (!emailAccountId) {
      throw new Error('Please specify emailAccountId!');
    }

    const url = `emails/canned-messages?email-account-id=${emailAccountId}`;
    return this._sendGet(url);
  }

  /**
   * Returns attachments for a given emailId.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * */
  getEmailAttachments(emailId) {
    if (!emailId) {
      throw new Error('Please specify emailId!');
    }

    const url = `emails/${emailId}/attachments`;
    return this._sendGet(url);
  }

  /**
   * Returns a URL to download an attachment for a given emailAttachmentId and attachmentFileName.
   * @param {string} emailId The unique identifier of the email the attachment belongs to.
   * @param {string} emailAttachmentId The unique identifier of the email attachment.
   * @param {string} attachmentFileName The filename of the attachment to be downloaded.
   * @returns {string} A rest API path to download an attachment for a given emailAttachmentId and attachmentFileName.
   * */
  getEmailAttachmentDownloadUrl(emailId, emailAttachmentId, attachmentFileName) {
    return this._getBaseUrl() + `emails/${emailId}/attachments/${emailAttachmentId}/download/${attachmentFileName}`;
  }

  getEmailAttachmentUploadUrl(emailThreadId?) {
    if (!emailThreadId) {
      return this._getBaseUrl() + 'email-threads/upload';
    }
    return this._getBaseUrl() + `email-threads/${emailThreadId}/upload`;
  }

  getFileUploadUrl() {
    return this._getBaseUrl() + 'uploads/upload';
  }

  /**
   * Returns departments with their operators.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * */
  getDepartmentsWithOperators() {
    return this._sendGet('departments');
  }

  getDepartment(departmentId) {
    if (!departmentId) {
      throw new Error('Please specify departmentId!');
    }

    const url = `departments/${departmentId}`;
    return this._sendGet(url);
  }

  getWebsiteDef(websiteDefId) {
    if (!websiteDefId) {
      throw new Error('Please specify websiteDefId!');
    }

    const url = `websites/${websiteDefId}`;
    return this._sendGet(url);
  }

  getWebSocketEndpoint(): Promise<UrlModel> {
    const url = 'domain/web-socket-url';
    return this._sendGet(url);
  }

  /**
   * Returns the Geo-Affinity aware ServerSet with Domain for the specified aid or username.
   * @param {Object} params Contains accountId or username.
   * - accountId The unique identifier of the account.
   * - username The unique name of the user.
   * @returns {Promise.<Object>} The promise of the raw data returned by the server.
   * @throws {Error} If accountId or username is not specified.
   */
  getServerEndpoints(params): Promise<ServerEndpointsModel> {
    params = params || {};
    const url = 'domain/server-endpoints';

    return this._sendGet(url, { queryString: params }, { accountAware: false });
  }

  /**
   * Returns operators with their availability.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   */
  getOperatorsWithAvailability() {
    return this._sendGet('operators');
  }

  getOperatorsNamesByDepartmentPermission(): Promise<OperatorNamesList[]> {
    return this._sendGet('operators/names');
  }

  getOperatorInitialGreetings(): Promise<OperatorInitialGreeting[]> {
    return this._sendGet('operators/initial-greetings');
  }

  getOperatorInitialGreetingSettings(): Promise<OperatorInitialGreetingSettings> {
    return this._sendGet('operators/initial-greeting-settings');
  }

  createNewChatPinInvite(pinType?: PinType): Promise<any> {
    let returnValue;
    if (pinType === null || pinType === undefined) {
      returnValue = this._sendPost('invites/pins');
    } else {
      const pinTypeParams = { pinType: PinType[pinType] };
      returnValue = this._sendPost('invites/pins', { queryString: pinTypeParams });
    }

    return returnValue.then((resp) => {
      resp.ItemID = resp.pinvitationId;
      return resp;
    });
  }

  getPendingChatPinInvites(): Promise<any> {
    return this._sendGet('invites/pins');
  }

  removePendingChatPinInvite(pinId: string): Promise<void> {
    if (!pinId) {
      throw new Error('Please specify pinId!');
    }

    return this._sendDelete(`invites/pins/${pinId}`);
  }

  createLensSession(sessionId) {
    const url = 'lens-session/';
    const params = { id: sessionId };

    return this._sendPost(url, { body: params });
  }

  /**
   * Returns the servicePlanType for the given service and account.
   * @param {string} serviceName The service name which plantype we want to get
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   * @private
   */
  getServicePlanTypeByServiceName(serviceName) {
    if (!serviceName) {
      throw new Error('Please specify serviceName!');
    }

    const url = `account/services/${serviceName}`;
    return this._sendGet(url);
  }

  /**
   * Returns the servicePlanTypes for all services for an account.
   * @returns {Promise.<object>} The promise of the raw data returned by the server.
   */
  getServicePlanTypesForAllServices() {
    const url = 'account/services';
    return this._sendGet(url);
  }

  /**
   * Returns all permissions for the current operator
   * @param {Array.<string>} askedPermissions
   * @returns {Promise.<RawUserPermissionModel[]>} The promise of the raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   */
  getOperatorActionPermissionsMap(askedPermissions: string[]): Promise<RawUserPermissionModel[]> {
    if (!askedPermissions) {
      throw new Error('Please specify a permission list!');
    }

    const url = `users/me/permissions?list=${askedPermissions}`;
    return this._sendGet(url);
  }

  /**
   * Returns all account permissions for the current operator
   * @param {Array.<string>} askedPermissions
   * @returns {Promise.<RawUserPermissionModel[]>} The promise of the raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   */
  getOperatorAccountPermissionsMap(askedPermissions: string[]): Promise<RawUserPermissionModel[]> {
    if (!askedPermissions) {
      throw new Error('Please specify a permission list!');
    }

    const url = `users/me/account-permissions?list=${askedPermissions}`;
    return this._sendGet(url);
  }

  /**
   * Returns the available integrations for the specified chat. If feature is not available for this account skip the HTTP interceptor. No user notification required.
   * @returns {Promise.<object>} The promise of JSON data returned by the server describing the available integrations.
   * @throws {Error} If the chatId is not specified.
   */
  getIntegrations(chatId: string, type: string): Promise<any> {
    if (!chatId) {
      throw new Error('Please specify the chatId!');
    }

    switch (type) {
      case WorkItemTypes.CHAT:
        return this._sendGet(`chats/${chatId}/integrations`, undefined, { skipHttpInterceptor: true });
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        return this._sendGet(`text-chats/${chatId}/integrations`, undefined, { skipHttpInterceptor: true });
    }
    throw new Error('Invalid work item type for integration');
  }

  /**
   * Returns the integration URL for requested chat-integration pair.
   * @returns {Promise.<object>} The promise containing the URL in JSON structure.
   * @throws {Error} If the chatId or the integrationId is missing.
   */
  getIntegrationUrl(chatId: string, integrationId: string, type: string): Promise<any> {
    if (!chatId) {
      throw new Error('Please specify the chatId!');
    }

    if (!integrationId) {
      throw new Error('Please specify the integrationId!');
    }

    switch (type) {
      case WorkItemTypes.CHAT:
        return this._sendGet(`chats/${chatId}/integrations/${integrationId}`);
      case WorkItemTypes.SMS:
      case WorkItemTypes.FACEBOOK:
      case WorkItemTypes.ASYNC_MESSAGE:
        return this._sendGet(`text-chats/${chatId}/integrations/${integrationId}`);
    }
    throw new Error('Invalid work item type for integration');
  }

  getRemoteHistoryItemsForChat(chatId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    return this._sendGet(`chats/${chatId}/rc-histories`);
  }

  addNewRemoteControlHistoryItem(chatId: string, rcSessionId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!rcSessionId) {
      throw new Error('Please specify rcSessionId!');
    }
    return this._sendPost(`chats/${chatId}/rc-histories/`, { body: { rcSessionId } });
  }

  closeRemoteControlHistoryItem(chatId: string, historyId: string, closedReasonType: RemoteControlCloseTypes) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!historyId) {
      throw new Error('Please specify RC historyId!');
    }
    if (typeof closedReasonType === 'undefined') {
      throw new Error('Please specify closedReasonType!');
    }
    return this._sendPost(`chats/${chatId}/rc-histories/${historyId}/close`, { body: { closedReasonType } });
  }

  startRemoteControlSessionWithAppletUrl(chatId: string, historyId: string, appletUrl: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!historyId) {
      throw new Error('Please specify RC historyId!');
    }
    if (!appletUrl) {
      throw new Error('Please specify appletUrl!');
    }
    return this._sendPost(`chats/${chatId}/rc-histories/${historyId}/start`, { body: { appletUrl } });
  }

  /**
   * Starts an Assisted Browsing session.
   * @returns {Promise.<object>} The promise containing the created assistedBrowsingId.
   * @throws {Error} If any parameter is not specified.
   */
  beginAssistedBrowsingSession(chatId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    return this._sendPost('assisted-browsing', { body: { chatId } });
  }

  /**
   * Starts a page synchronization in an Assisted Browsing session.
   * @returns {Promise.<object>} The promise containing the url for the viewer frame.
   * @throws {Error} If any parameter is not specified.
   */
  startAssistedBrowsingSynchronization(assistedBrowsingId: string, visitId: string, pageViewId: string) {
    if (!assistedBrowsingId) {
      throw new Error('Please specify assistedBrowsingId!');
    }
    if (!visitId) {
      throw new Error('Please specify visitId!');
    }
    if (!pageViewId) {
      throw new Error('Please specify pageViewId!');
    }
    return this._sendPost(`assisted-browsing/${assistedBrowsingId}/page-view`, { body: { visitId, pageViewId } });
  }

  /**
   * Ends an Assisted Browsing session.
   * @returns {Promise.<object>} The promise containing nothing.
   * @throws {Error} If any parameter is not specified.
   */
  endAssistedBrowsingSession(assistedBrowsingId: string) {
    if (!assistedBrowsingId) {
      throw new Error('Please specify assistedBrowsingId!');
    }
    return this._sendDelete(`assisted-browsing/${assistedBrowsingId}`);
  }

  /**
   * Pushes an url to the visitor in an Assisted Browsing session.
   * @returns {Promise.<object>} The promise containing nothing.
   * @throws {Error} If any parameter is not specified.
   */
  pushAssistedBrowsingPage(assistedBrowsingId: string, visitId: string, pageViewId: string, url: string) {
    if (!assistedBrowsingId) {
      throw new Error('Please specify assistedBrowsingId!');
    }
    if (!visitId) {
      throw new Error('Please specify visitId!');
    }
    if (!pageViewId) {
      throw new Error('Please specify pageViewId!');
    }
    if (!url) {
      throw new Error('Please specify url!');
    }
    return this._sendPost(`assisted-browsing/${assistedBrowsingId}/page-view/${pageViewId}/push-page`, {
      body: { visitId, url }
    });
  }

  /**
   * Navigates back the visitor in an Assisted Browsing session.
   * @returns {Promise.<object>} The promise containing nothing.
   * @throws {Error} If any parameter is not specified.
   */
  navigateAssistedBrowsingBack(assistedBrowsingId: string, visitId: string, pageViewId: string) {
    if (!assistedBrowsingId) {
      throw new Error('Please specify assistedBrowsingId!');
    }
    if (!visitId) {
      throw new Error('Please specify visitId!');
    }
    if (!pageViewId) {
      throw new Error('Please specify pageViewId!');
    }
    return this._sendPost(`assisted-browsing/${assistedBrowsingId}/page-view/${pageViewId}/back`, {
      body: { visitId }
    });
  }

  /**
   * Navigates forward the visitor in an Assisted Browsing session.
   * @returns {Promise.<object>} The promise containing nothing.
   * @throws {Error} If any parameter is not specified.
   */
  navigateAssistedBrowsingForward(assistedBrowsingId: string, visitId: string, pageViewId: string) {
    if (!assistedBrowsingId) {
      throw new Error('Please specify assistedBrowsingId!');
    }
    if (!visitId) {
      throw new Error('Please specify visitId!');
    }
    if (!pageViewId) {
      throw new Error('Please specify pageViewId!');
    }
    return this._sendPost(`assisted-browsing/${assistedBrowsingId}/page-view/${pageViewId}/forward`, {
      body: { visitId }
    });
  }

  /**
   * Reloads Assisted Browsing page.
   * @returns {Promise.<object>} The promise containing nothing.
   * @throws {Error} If any parameter is not specified.
   */
  reloadAssistedBrowsingPage(assistedBrowsingId: string, visitId: string, pageViewId: string) {
    if (!assistedBrowsingId) {
      throw new Error('Please specify assistedBrowsingId!');
    }
    if (!visitId) {
      throw new Error('Please specify visitId!');
    }
    if (!pageViewId) {
      throw new Error('Please specify pageViewId!');
    }
    return this._sendPost(`assisted-browsing/${assistedBrowsingId}/page-view/${pageViewId}/reload`, {
      body: { visitId }
    });
  }

  /**
   * Get Assisted Browsing bookmarks.
   * @returns {Promise.<object>} The promise containing bookmarks.
   */
  getBookmarks() {
    return this._sendGet('assisted-browsing/bookmarks');
  }

  /**
   * Get assistedBrowsingId.
   * @returns {Promise.<object>} The promise containing assisted browsing id.
   * @throws {Error} If any parameter is not specified.
   */
  getAssistedBrowsingId(chatId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    return this._sendGet(`chats/${chatId}/assisted-browsing`, undefined, { skipHttpInterceptor: true });
  }

  getRemoteControlHistoryForAccount() {
    return this._sendGet('chats/recordings');
  }

  getSalesforceGroups(chatId: string, searchTerm?: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const channelId: string = 'chat';
    return this._sendGet(`salesforce/objects/${channelId}/${chatId}`, {
      ...(searchTerm && { queryString: { term: searchTerm } })
    });
  }

  getSalesforceItemDetails(itemId: string) {
    if (!itemId) {
      throw new Error('Please specify itemId!');
    }

    return this._sendGet(`salesforce/object/${itemId}`);
  }

  linkSalesforceObjectToChat(chatId: string, objectId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!objectId) {
      throw new Error('Please specify Salesforce objectId!');
    }

    const channelId: string = 'chat';
    return this._sendPost(`salesforce/link/${channelId}/${chatId}/${objectId}`);
  }

  unlinkSalesforceObjectFromChat(chatId: string, objectId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!objectId) {
      throw new Error('Please specify Salesforce objectId!');
    }

    const channelId: string = 'chat';
    return this._sendDelete(`salesforce/link/${channelId}/${chatId}/${objectId}`);
  }

  getSalesforceForm(chatId: string, salesforceObjectType: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!salesforceObjectType) {
      throw new Error('Please specify salesforceObjectType!');
    }

    const channelId: string = 'chat';
    return this._sendGet(`salesforce/form/${channelId}/${chatId}/${salesforceObjectType}`);
  }

  createSalesforceObject(chatId: string, salesforceObjectType: string, salesforceSaveObject: SalesforceSaveObject) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!salesforceObjectType) {
      throw new Error('Please specify salesforceObjectType!');
    }
    if (!salesforceSaveObject) {
      throw new Error('Please specify salesforceSaveObject!');
    }

    const channelId: string = 'chat';
    return this._sendPost(`salesforce/object/${channelId}/${chatId}/${salesforceObjectType}`, {
      body: salesforceSaveObject
    });
  }

  getSalesforceReferenceItems(
    salesforceObjectType: string,
    searchTerm: string,
    contextObjectType?: string
  ): Promise<GetSalesforceReferenceItemsResult> {
    if (!salesforceObjectType) {
      throw new Error('Please specify the salesforceObjectType!');
    }

    if (!searchTerm) {
      throw new Error('Please specify the searchTerm!');
    }

    return this._sendGet(`salesforce/objects/${salesforceObjectType}`, {
      queryString: { term: searchTerm, contextObjectType }
    });
  }

  /**
   * Get Nanorep account info.
   * @returns {Promise.<object>} The promise containing Nanorep account info.
   */
  getNanorepAccountInfo(): Promise<NanorepAccountInfoModel> {
    return this._sendGet('nanorep/account');
  }

  /**
   * Sends the specified message to propose an article.
   * @param {string} chatId - The unique identifier of the chat to assign.
   * @param {string} body - The article body.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatId or the article body is not specified.
   */
  sendNanorepArticleSuggestion(chatId: string, body: string, lastVisitorMessage: string): Promise<void> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (!body) {
      throw new Error('Please specify article body!');
    }

    if (!lastVisitorMessage) {
      throw new Error('Please specify last visitor message!');
    }
    const apiMethod = 'nanorep/article';
    const requestBody = { chatId, body, lastVisitorMessage };
    return this._sendPost(apiMethod, { body: requestBody });
  }

  sendNanorepArticleComment(articleId: string, comment: string): Promise<void> {
    if (!articleId) {
      throw new Error('Please specify articleId!');
    }
    if (!comment) {
      throw new Error('Please specify comment!');
    }
    const apiMethod = 'nanorep/comment';
    const requestBody = { articleId, comment };
    return this._sendPost(apiMethod, { body: requestBody });
  }

  getInactiveChats(
    departmentIds: string[],
    folderIds: string,
    fromDate: number,
    toDate: number,
    operatorIds: string[] | null
  ): Promise<any[]> {
    if (!departmentIds) {
      throw new Error('Please specify departmentIds!');
    }

    if (!folderIds) {
      throw new Error('Please specify folderIds!');
    }

    if (!fromDate) {
      throw new Error('Please specify fromDate!');
    }

    if (!toDate) {
      throw new Error('Please specify toDate!');
    }

    const url = 'chats/inactive';
    const requestBody: any = { departmentIds, folderIds, fromDate, toDate };
    if (operatorIds && operatorIds.length) {
      requestBody.operatorIds = operatorIds;
    }
    return this._sendPost(url, { body: requestBody });
  }

  getActiveChatCounts(): Promise<ActiveWorkItemCountModel[]> {
    const url = 'operators/chat/count';
    return this._sendGet(url);
  }

  getInactiveTextChats(
    departmentIds: string[],
    folderIds: string,
    fromDate: number,
    toDate: number,
    operatorIds: string[] | null
  ): Promise<any[]> {
    if (!departmentIds) {
      throw new Error('Please specify departmentIds!');
    }

    if (!folderIds) {
      throw new Error('Please specify folderIds!');
    }

    if (!fromDate) {
      throw new Error('Please specify fromDate!');
    }

    if (!toDate) {
      throw new Error('Please specify toDate!');
    }

    const url = 'text-chats/inactive';
    const requestBody: any = { departmentIds, folderIds, fromDate, toDate };
    if (operatorIds && operatorIds.length) {
      requestBody.operatorIds = operatorIds;
    }
    return this._sendPost(url, { body: requestBody });
  }

  getActiveTextChatCounts(): Promise<ActiveWorkItemCountModel[]> {
    const url = 'operators/text-chat/count';
    return this._sendGet(url);
  }

  getInactiveEmails(
    departmentIds: string[],
    folderIds: string,
    fromDate: number,
    toDate: number,
    operatorIds: string[] | null
  ): Promise<any[]> {
    if (!departmentIds) {
      throw new Error('Please specify departmentIds!');
    }

    if (!folderIds) {
      throw new Error('Please specify folderIds!');
    }

    if (!fromDate) {
      throw new Error('Please specify fromDate!');
    }

    if (!toDate) {
      throw new Error('Please specify toDate!');
    }

    const url = 'email-threads/inactive';
    const requestBody: any = { departmentIds, folderIds, fromDate, toDate };
    if (operatorIds && operatorIds.length) {
      requestBody.operatorIds = operatorIds;
    }
    return this._sendPost(url, { body: requestBody });
  }

  /**
   * Starts a discussion with an other operator.
   * @param {string} itemId - The unique identifier of the workItem.
   * @param {string} helperOperatorId - The helper operator's id.
   * @param {string} workItemType - The WorkItem's type to check permissions for.
   * @param {string} selectedItemType - Selected item's type.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If any parameter is not specified.
   */
  initDiscussion(
    workItemId: string,
    helperOperatorId: string,
    workItemType: number,
    selectedItemType: string
  ): Promise<InitDiscussionResponseModel> {
    if (!workItemId) {
      throw new Error('Please specify workItemId!');
    }
    if (!helperOperatorId) {
      throw new Error('Please specify helperOperatorId!');
    }

    if (!workItemType) {
      throw new Error('Please specify workItemType!');
    }

    if (!selectedItemType) {
      throw new Error('Please specify selectedItemType!');
    }

    const requestBody = { workItemId, helperOperatorId, itemType: workItemType, idType: selectedItemType };
    return this._sendPost('discussion/create', { body: requestBody });
  }

  /**
   * Cancels a discussion.
   * @param {string} discussionId - The unique identifier of the discussion.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If any parameter is not specified.
   */
  cancelDiscussion(discussionId: string): Promise<void> {
    if (!discussionId) {
      throw new Error('Please specify discussionId!');
    }

    const url = `discussion/${discussionId}/cancel`;
    return this._sendPost(url);
  }

  /**
   * Ends a discussion.
   * @param {string} discussionId - The unique identifier of the discussion.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If any parameter is not specified.
   */
  endDiscussion(discussionId: string): Promise<void> {
    if (!discussionId) {
      throw new Error('Please specify discussionId!');
    }

    const url = `discussion/${discussionId}/end`;
    return this._sendPost(url);
  }

  /**
   * Add message to discussion.
   * @param {string} discussionId - The unique identifier of the discussion.
   * @param {string} message - Discussion message.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If any parameter is not specified.
   */
  sendDiscussionMessage(discussionId: string, message: string): Promise<void> {
    if (!discussionId) {
      throw new Error('Please specify discussionId!');
    }

    const url = `discussion/${discussionId}/message`;
    const requestBody = { message };
    return this._sendPost(url, { body: requestBody });
  }

  /**
   * Accepts a discussion.
   * @param {string} discussionId - The unique identifier of the discussion.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If any parameter is not specified.
   */
  acceptDiscussion(discussionId: string): Promise<void> {
    if (!discussionId) {
      throw new Error('Please specify discussionId!');
    }
    const url = `discussion/${discussionId}/accept`;
    return this._sendPost(url);
  }

  setFlag(workItemId: string, state: boolean): Promise<void> {
    if (!workItemId) {
      throw new Error('Please specify workItemId!');
    }
    const url = `chats/${workItemId}/flag`;
    const requestBody = { state };

    return this._sendPost(url, { body: requestBody });
  }

  /** Blocks the chat with the specified chatId.
   * @param {string} chatId - The unique identifier of the chat to end.
   * @returns {Promise} The promise for the result of the HTTP call.
   * @throws {Error} If chatId is not specified.
   */
  blockChat(chatId: string) {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/blockIp`;
    return this._sendPost(url);
  }

  getAgentStatsSettings(): Promise<AgentStatIndicator[]> {
    const url = 'agent-stat/settings';
    return this._sendGet(url);
  }

  getAgentStat(indicator: AgentStatIndicator, timeZoneOffset: number, dayOfWeek: number): Promise<AgentStat> {
    if (!indicator) {
      throw new Error('Please specify indicator!');
    }
    if (timeZoneOffset === null || timeZoneOffset === undefined) {
      throw new Error('Please specify timeZoneOffset!');
    }
    if (dayOfWeek === null || dayOfWeek === undefined) {
      throw new Error('Please specify dayOfWeek!');
    }
    const url = 'agent-stat';
    return this._sendPost(url, {
      body: { agentStatSettings: indicator, timeZoneOffsetInMinutes: timeZoneOffset, dayOfWeek }
    });
  }

  getAgentStats(
    indicators: AgentStatIndicator[],
    timeZoneOffset: number,
    dayOfWeek: number
  ): Promise<Record<number, AgentStat>> {
    if (!indicators || indicators.length === 0) {
      throw new Error('Please specify indicators!');
    }
    if (timeZoneOffset === null || timeZoneOffset === undefined) {
      throw new Error('Please specify timeZoneOffset!');
    }
    if (dayOfWeek === null || dayOfWeek === undefined) {
      throw new Error('Please specify dayOfWeek!');
    }
    const url = 'agent-stat/agent-stats';
    return this._sendPost(url, {
      body: { agentStatSettingsList: indicators, timeZoneOffsetInMinutes: timeZoneOffset, dayOfWeek }
    });
  }

  getAgentLoads(departmentIds: string[]): Promise<AgentLoads> {
    if (!departmentIds) {
      throw new Error('Please specify departmentIds!');
    }

    const url = 'operators/agent-load';
    return this._sendGet(url, {
      queryString: {
        departmentIds
      }
    });
  }

  getCapacity(operatorIds: string[]): Promise<AgentCapacities> {
    if (!operatorIds) {
      throw new Error('Please specify operatorIds!');
    }

    const url = 'operators/capacity';
    return this._sendGet(url, {
      queryString: {
        operatorIds
      }
    });
  }

  getInitialCapacity(departmentIds: string[]): Promise<AgentCapacities> {
    if (!departmentIds) {
      throw new Error('Please specify departmentIds!');
    }

    const url = 'operators/initial-capacity';
    return this._sendGet(url, {
      queryString: {
        departmentIds
      }
    });
  }

  getDepartmentLoads(departmentIds: string[], serviceTypes: ServiceTypes[]): Promise<DepartmentLoadsModel> {
    if (!departmentIds) {
      throw new Error('Please specify departmentIds!');
    }

    let query = `?departmentIds=${departmentIds}`;
    if (serviceTypes && serviceTypes.length > 0) {
      query += `&serviceTypes=${serviceTypes}`;
    }

    const url = `departments/department-load${query}`;
    return this._sendGet(url);
  }

  getAssignmentHistoryByChatId(chatId: string): Promise<AssignmentHistoryModel[]> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    const url = `chats/${chatId}/assignment-history`;
    return this._sendGet(url);
  }

  getAssignmentHistoryByTextChatId(textChatId: string): Promise<AssignmentHistoryModel[]> {
    if (!textChatId) {
      throw new Error('Please specify textChatId!');
    }

    const url = `text-chats/${textChatId}/assignment-history`;
    return this._sendGet(url);
  }

  startVendorVideoSession(chatId: string, enforced: boolean, autoStart: boolean): Promise<VendorVideoSessionResponse> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }
    if (typeof enforced !== 'boolean') {
      throw new Error('Please specify enforced!');
    }
    if (typeof autoStart !== 'boolean') {
      throw new Error('Please specify autoStart!');
    }

    return this._sendPost(`chats/${chatId}/vendor-video/start`, { body: { enforced, autoStart } });
  }

  getVendorVideoSession(chatId: string): Promise<VendorVideoSessionResponse> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    return this._sendGet(`chats/${chatId}/vendor-video`);
  }

  joinVendorRemoteControlSession(chatId: string): Promise<VendorRemoteControlSessionResponse> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    return this._sendPost(`chats/${chatId}/vendor-remote-control/join`);
  }

  cancelVendorRemoteControlSession(chatId: string): Promise<void> {
    if (!chatId) {
      throw new Error('Please specify chatId!');
    }

    return this._sendPost(`chats/${chatId}/vendor-remote-control/cancel`);
  }

  public sendLog(logRequest: LogModel): Promise<void> {
    const url = 'logger';
    return this._sendPost(url, { body: logRequest }, { skipHttpInterceptor: true });
  }

  /**
   * Returns the prefix for a REST API endpoint based on the current configuration. Always ends with a '/'.
   * (e.g. https://rest-alphaav1.boldchat.com/aid/0/v1/)
   * @param {boolean} accountAware The endpoint should contain accountId (default true)
   * @returns {string} The absolute URL to the API root.
   * @throws {Error} If apiSelector, accountId or restSubDomain in the current configuration file is falsy.
   * @private
   */
  private _getBaseUrl(accountAware: boolean = true) {
    if (accountAware && !this.accountId) {
      throw new Error('The restApi should be initialized with accountId for this request.');
    }

    return accountAware
      ? `${this._bcConfig.getRestApiHost()}/aid/${this.accountId}/${API_VERSION}/`
      : `${this._bcConfig.getRestApiHost()}/${API_VERSION}/`;
  }

  /**
   * Returns the absolute URL for the specified API method.
   * @param {string} apiMethod The relative path for the API method (e.g. "chat/answer");
   * @returns {string} The absolute URL for the API method.
   * @throws {Error} If the apiMethod is not specified.
   * @private
   */
  private _getEndpointUrl(apiMethod, accountAware: boolean = true) {
    if (!apiMethod) {
      throw new Error('Please specify apiMethod!');
    }

    const baseUrl = this._getBaseUrl(accountAware);
    return `${baseUrl}${apiMethod}`;
  }

  /**
   * Sends a HTTP GET request to the specified API method with the specified URL parameters.
   * @param {string} apiMethod The relative path for the API method (e.g. "chat/answer");
   * @param {Object.<ApiParameters>} parameters Optional: The query string and request body
   * @param {Object.<ApiConfig>} config Optional
   * @returns {Promise<object>} The raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   * @private
   */
  private _sendGet(
    apiMethod,
    parameters?: ApiParameters,
    config: ApiConfig = { skipHttpInterceptor: false, accountAware: true }
  ) {
    let { url, headers } = this._buildRequestParameters(apiMethod, parameters, config);

    if (!apiMethod) {
      throw new Error('Please specify apiMethod!');
    }

    return this._bcApi.get(url, headers);
  }

  /**
   * Sends a HTTP POST request to the specified API method with the specified parameters in the request body.
   * @param {string} apiMethod The relative path for the API method (e.g. "chat/answer");
   * @param {Object.<ApiParameters>} parameters Optional: The query string and request body
   * @param {Object.<ApiConfig>} config Optional
   * @returns {Promise<object>} The raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   * @private
   */
  private _sendPost(apiMethod, parameters?: ApiParameters, config?: ApiConfig) {
    let { url, requestBody, headers } = this._buildRequestParameters(apiMethod, parameters, config);

    return this._bcApi.post(url, requestBody, headers);
  }

  /**
   * Sends a HTTP PUT request to the specified API method with the specified parameters in the request body.
   * @param {string} apiMethod The relative path for the API method (e.g. "chat/answer");
   * @param {Object.<ApiParameters>} parameters Optional: The query string and request body
   * @param {Object.<ApiConfig>} config Optional
   * @returns {Promise<object>} The raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   * @private
   */
  // @ts-ignore: unused basic HTTP operation
  private _sendPut(apiMethod, parameters?: ApiParameters, config?: ApiConfig) {
    let { url, requestBody, headers } = this._buildRequestParameters(apiMethod, parameters, config);

    return this._bcApi.put(url, requestBody, headers);
  }

  /**
   * Sends a HTTP DELETE request to the specified API method with the specified URL parameters.
   * @param {string} apiMethod The relative path for the API method (e.g. "chat/answer");
   * @param {Object.<ApiParameters>} parameters Optional: The query string and request body
   * @param {Object.<ApiConfig>} config Optional
   * @returns {Promise<object>} The raw data returned by the server.
   * @throws {Error} If the apiMethod is not specified.
   * @private
   */
  private _sendDelete(apiMethod, parameters?: ApiParameters, config?: ApiConfig) {
    let { url, headers } = this._buildRequestParameters(apiMethod, parameters, config);

    return this._bcApi.delete(url, headers);
  }

  private _buildRequestParameters(
    apiMethod,
    parameters?: ApiParameters,
    config: ApiConfig = { skipHttpInterceptor: false, accountAware: true }
  ) {
    if (!apiMethod) {
      throw new Error('Please specify apiMethod!');
    }

    let { skipHttpInterceptor, accountAware } = config;
    const queryString = (parameters && parameters.queryString) || void 0;
    const bodyParams = parameters && parameters.body;

    const url = this._getEndpointUrl(apiMethod, accountAware);
    const headers = {
      params: queryString,
      skipHttpInterceptor: skipHttpInterceptor || false
    };

    // NOTE: If you leave the request body undefined, $http will not send the Content-Type header, and the server
    //       will return with a "No 'Access-Control-Allow-Origin' header is present on the requested resource" error.
    const requestBody = bodyParams || null;

    return { url, requestBody, headers };
  }
}
